/**
 * Client data models with Zod schemas
 * Explicit schema definitions with parsing validation
 */
import { parseISO } from "date-fns";
import { z } from "zod";

// Domain schema (camelCase)
export const ClientSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  notes: z.string().nullable(),
  userId: z.string().nullable(),
  businessId: z.string().nullable(),
});

const CreateClientRequestSchema = ClientSchema.omit({
  id: true,
  createdAt: true,
}).partial({
  firstName: true,
  lastName: true,
  email: true,
  notes: true,
  userId: true,
});

const UpdateClientRequestSchema = ClientSchema.omit({
  id: true,
  createdAt: true,
  businessId: true,
}).partial();

// DTO schema (snake_case)
export const ClientDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  phone_number: z.string().nullable(),
  notes: z.string().nullable(),
  user_id: z.string().nullable(),
  business_id: z.string().nullable(),
});

export const CreateClientDtoSchema = ClientDtoSchema.omit({
  id: true,
  created_at: true,
}).partial({
  first_name: true,
  last_name: true,
  email: true,
  notes: true,
  user_id: true,
});

export const UpdateClientDtoSchema = ClientDtoSchema.omit({
  id: true,
  created_at: true,
  business_id: true,
}).partial();

// Infer DTO types
export type ClientDto = z.infer<typeof ClientDtoSchema>;
export type CreateClientDto = z.infer<typeof CreateClientDtoSchema>;
export type UpdateClientDto = z.infer<typeof UpdateClientDtoSchema>;

// Infer domain types
export type Client = z.infer<typeof ClientSchema>;
export type CreateClientRequest = z.infer<typeof CreateClientRequestSchema>;
export type UpdateClientRequest = z.infer<typeof UpdateClientRequestSchema>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromClientDtoToClient(dto: ClientDto): Client {
  const clientDto = ClientDtoSchema.parse(dto);

  return {
    id: clientDto.id,
    createdAt: parseISO(clientDto.created_at),
    firstName: clientDto.first_name,
    lastName: clientDto.last_name,
    email: clientDto.email,
    phoneNumber: clientDto.phone_number,
    notes: clientDto.notes,
    userId: clientDto.user_id,
    businessId: clientDto.business_id,
  };
}

export function fromCreateClientRequestToCreateClientDto(
  request: CreateClientRequest
): CreateClientDto {
  const createClientRequest = CreateClientRequestSchema.parse(request);

  return {
    first_name: createClientRequest.firstName,
    last_name: createClientRequest.lastName,
    phone_number: createClientRequest.phoneNumber,
    email: createClientRequest.email,
    notes: createClientRequest.notes,
    user_id: createClientRequest.userId,
    business_id: createClientRequest.businessId,
  };
}

export function fromUpdateClientRequestToUpdateClientDto(
  request: UpdateClientRequest
): UpdateClientDto {
  const updateClientRequest = UpdateClientRequestSchema.parse(request);

  return {
    first_name: updateClientRequest.firstName,
    last_name: updateClientRequest.lastName,
    phone_number: updateClientRequest.phoneNumber,
    email: updateClientRequest.email,
    notes: updateClientRequest.notes,
  };
}
